import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import { CustomTable } from "../../componentes/table/CustomTable";
import TelemetriaService from "../../services/resource/telemetriaService";
import FilialService from "../../services/resource/filialService";
import FuncionarioService from "../../services/resource/funcionarioService";

const FileDownload = require("js-file-download");

const META_SEGURANCA_VIARIA = 98;

const ORIGENS = {
    TRIXLOG: { label: "Trixlog", icon: "fa fa-cloud-download-alt", cls: "border-primary/20 bg-primary/10 text-primary" },
    MANUAL: { label: "Manual", icon: "fa fa-user-edit", cls: "border-border bg-muted text-muted-foreground" },
    ARQUIVO: { label: "Planilha", icon: "fa fa-file-excel", cls: "border-success/30 bg-success/10 text-success" },
};

const inputCls = "w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/15";

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// competência atual: de 25 do mês anterior a 24 do mês corrente (ou do próximo, a partir do dia 25)
function competenciaAtual() {
    const hoje = new Date();
    const base = hoje.getDate() >= 25 ? new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1) : new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    return {
        inicio: toIso(new Date(base.getFullYear(), base.getMonth() - 1, 25)),
        fim: toIso(new Date(base.getFullYear(), base.getMonth(), 24)),
    };
}

const fmtData = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.substring(0, 10).split("-");
    return `${d}/${m}/${y}`;
};

const fmtDataHora = (iso) => {
    if (!iso) return "—";
    const [data, hora] = iso.split("T");
    return `${fmtData(data)} ${hora?.substring(0, 5) ?? ""}`;
};

const fmtNota = (v) => Number(v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 });

const erroDe = (e, padrao) => e?.response?.data?.message || padrao;

function Campo({ label, children, dica }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-foreground/80">{label}</label>
            {children}
            {dica && <span className="text-[10px] text-muted-foreground">{dica}</span>}
        </div>
    );
}

function Kpi({ label, valor, icon, cls }) {
    return (
        <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 shadow-card ${cls}`}>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-card/70 text-lg">
                <i className={icon} />
            </div>
            <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest opacity-60">{label}</p>
                <p className="text-2xl font-bold leading-tight tabular-nums">{valor}</p>
            </div>
        </div>
    );
}

export default function TelemetriaPage() {
    const service = useMemo(() => new TelemetriaService(), []);
    const filialService = useMemo(() => new FilialService(), []);
    const funcionarioService = useMemo(() => new FuncionarioService(), []);

    const [periodo, setPeriodo] = useState(competenciaAtual);
    const [filialId, setFilialId] = useState("");
    const [busca, setBusca] = useState("");
    const [origem, setOrigem] = useState("");
    const [somenteEditadas, setSomenteEditadas] = useState(false);

    const [filiais, setFiliais] = useState([]);
    const [medicoes, setMedicoes] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalForm, setModalForm] = useState(false);
    const [modalImportar, setModalImportar] = useState(false);
    const [modalTrixlog, setModalTrixlog] = useState(false);

    useEffect(() => {
        filialService.listar().then(res => setFiliais((res.data ?? []).filter(f => f.ativo !== false)));
    }, [filialService]);

    const carregar = useCallback(() => {
        if (!periodo.inicio || !periodo.fim) return;
        setLoading(true);
        service.listarMedicoes({ inicio: periodo.inicio, fim: periodo.fim, filialId: filialId || null })
            .then(res => setMedicoes(res.data ?? []))
            .catch(e => toast.error(erroDe(e, "Erro ao carregar as medições.")))
            .finally(() => setLoading(false));
    }, [service, periodo, filialId]);

    useEffect(() => { carregar(); }, [carregar]);

    const filtradas = useMemo(() => {
        const q = busca.trim().toLowerCase();
        return medicoes.filter(m =>
            (!origem || m.origem === origem)
            && (!somenteEditadas || m.editadoManualmente)
            && (!q
                || m.funcionarioNome?.toLowerCase().includes(q)
                || m.funcionarioCodigo?.toLowerCase().includes(q)
                || m.motoristaNome?.toLowerCase().includes(q)));
    }, [medicoes, busca, origem, somenteEditadas]);

    const kpis = useMemo(() => {
        const comFuncionario = filtradas.filter(m => m.funcionarioId);
        const media = filtradas.length
            ? filtradas.reduce((s, m) => s + (m.segurancaViaria ?? 0), 0) / filtradas.length
            : 0;
        return {
            total: filtradas.length,
            funcionarios: new Set(comFuncionario.map(m => m.funcionarioId)).size,
            media,
            editadas: filtradas.filter(m => m.editadoManualmente).length,
            semFuncionario: filtradas.length - comFuncionario.length,
        };
    }, [filtradas]);

    /* ---------------- formulário (cadastro e edição) ---------------- */

    const [editando, setEditando] = useState(null);
    const [form, setForm] = useState({});
    const [funcionarios, setFuncionarios] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [erroForm, setErroForm] = useState("");

    const carregarFuncionarios = useCallback((id) => {
        setFuncionarios([]);
        if (!id) return;
        funcionarioService.listar({ filialId: id }).then(res => setFuncionarios(res.data ?? []));
    }, [funcionarioService]);

    const abrirNova = () => {
        setEditando(null);
        setForm({ filialId: filialId ? Number(filialId) : null, funcionarioId: null, data: toIso(new Date()), segurancaViaria: "", manutencao: "", performance: "" });
        carregarFuncionarios(filialId);
        setErroForm("");
        setModalForm(true);
    };

    const abrirEdicao = (m) => {
        setEditando(m);
        setForm({
            filialId: m.filialId, funcionarioId: m.funcionarioId, data: m.data,
            segurancaViaria: m.segurancaViaria, manutencao: m.manutencao, performance: m.performance,
        });
        carregarFuncionarios(m.filialId);
        setErroForm("");
        setModalForm(true);
    };

    const alterarFilial = (id) => {
        setForm(f => ({ ...f, filialId: id, funcionarioId: null }));
        carregarFuncionarios(id);
    };

    const numero = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));

    const salvar = () => {
        if (!form.funcionarioId) { setErroForm("Selecione o funcionário."); return; }
        if (!form.data) { setErroForm("Informe a data."); return; }
        const seguranca = numero(form.segurancaViaria);
        if (seguranca === null) { setErroForm("Informe a nota de segurança viária."); return; }

        const dto = {
            funcionarioId: form.funcionarioId,
            data: form.data,
            segurancaViaria: seguranca,
            manutencao: numero(form.manutencao),
            performance: numero(form.performance),
        };

        setSalvando(true);
        setErroForm("");
        const chamada = editando ? service.atualizarMedicao(editando.id, dto) : service.criarMedicao(dto);
        chamada
            .then(() => {
                toast.success(editando ? "Medição atualizada." : "Medição cadastrada.");
                setModalForm(false);
                carregar();
            })
            .catch(e => setErroForm(erroDe(e, "Erro ao salvar a medição.")))
            .finally(() => setSalvando(false));
    };

    const excluir = (m) => {
        const texto = `Excluir a medição de ${m.funcionarioNome ?? m.motoristaNome ?? "—"} em ${fmtData(m.data)}?`
            + (m.origem === "TRIXLOG" ? "\n\nEla veio do Trixlog e voltará na próxima sincronização do período." : "");
        if (!window.confirm(texto)) return;
        service.removerMedicao(m.id)
            .then(() => { toast.success("Medição excluída."); carregar(); })
            .catch(e => toast.error(erroDe(e, "Erro ao excluir a medição.")));
    };

    /* ---------------- importação por planilha ---------------- */

    const [arquivo, setArquivo] = useState(null);
    const [filialImportacao, setFilialImportacao] = useState("");
    const [importando, setImportando] = useState(false);
    const [resultadoImportacao, setResultadoImportacao] = useState(null);

    const abrirImportacao = () => {
        setArquivo(null);
        setResultadoImportacao(null);
        setFilialImportacao(filialId ? String(filialId) : "");
        setModalImportar(true);
    };

    const baixarModelo = () => {
        service.baixarModelo()
            .then(res => FileDownload(res.data, "modelo-telemetria.xlsx"))
            .catch(() => toast.error("Erro ao baixar o modelo."));
    };

    const importar = () => {
        if (!filialImportacao) { toast.warning("Selecione a filial das medições."); return; }
        if (!arquivo) { toast.warning("Selecione a planilha."); return; }
        setImportando(true);
        setResultadoImportacao(null);
        service.importarMedicoes(arquivo, filialImportacao)
            .then(res => { setResultadoImportacao(res.data); carregar(); })
            .catch(e => toast.error(erroDe(e, "Erro ao importar a planilha.")))
            .finally(() => setImportando(false));
    };

    /* ---------------- sincronização com o Trixlog ---------------- */

    const [periodoTrixlog, setPeriodoTrixlog] = useState(periodo);
    const [sincronizando, setSincronizando] = useState(false);
    const [resultadoTrixlog, setResultadoTrixlog] = useState(null);
    const [erroTrixlog, setErroTrixlog] = useState("");

    const abrirTrixlog = () => {
        setPeriodoTrixlog(periodo);
        setResultadoTrixlog(null);
        setErroTrixlog("");
        setModalTrixlog(true);
    };

    const sincronizar = () => {
        setSincronizando(true);
        setResultadoTrixlog(null);
        setErroTrixlog("");
        service.sincronizarTrixlog(periodoTrixlog)
            .then(res => { setResultadoTrixlog(res.data); carregar(); })
            .catch(e => setErroTrixlog(erroDe(e, "Erro ao sincronizar com o Trixlog.")))
            .finally(() => setSincronizando(false));
    };

    /* ---------------- tabela ---------------- */

    const columns = useMemo(() => [
        {
            key: "data", label: "Data", sortable: true,
            render: (v) => <span className="font-semibold tabular-nums">{fmtData(v)}</span>,
        },
        { key: "filialNome", label: "Filial", sortable: true },
        {
            key: "funcionarioNome", label: "Funcionário", sortable: true,
            render: (v, row) => v
                ? <div>
                    <p className="font-semibold text-foreground">{v}</p>
                    <p className="text-[10px] text-muted-foreground">{row.funcionarioCodigo}</p>
                </div>
                : <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-600">
                    <i className="fa fa-unlink text-[9px]" /> Sem vínculo
                </span>,
        },
        { key: "motoristaNome", label: "Condutor (Trixlog)", sortable: true, render: (v) => v ?? "—" },
        {
            key: "segurancaViaria", label: "Seg. Viária", sortable: true, center: true,
            render: (v) => (
                <span className={`font-bold tabular-nums ${v >= META_SEGURANCA_VIARIA ? "text-success" : "text-destructive"}`}>
                    {fmtNota(v)}
                </span>
            ),
        },
        { key: "manutencao", label: "Manutenção", sortable: true, center: true, render: (v) => <span className="tabular-nums">{fmtNota(v)}</span> },
        { key: "performance", label: "Performance", sortable: true, center: true, render: (v) => <span className="tabular-nums">{fmtNota(v)}</span> },
        {
            key: "origem", label: "Origem", sortable: true,
            render: (v) => {
                const o = ORIGENS[v] ?? ORIGENS.TRIXLOG;
                return (
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${o.cls}`}>
                        <i className={`${o.icon} text-[9px]`} /> {o.label}
                    </span>
                );
            },
        },
        {
            key: "editadoManualmente", label: "Proteção", sortable: true,
            render: (v, row) => v
                ? <span
                    title={`Editada${row.atualizadoPor ? " por " + row.atualizadoPor : ""} em ${fmtDataHora(row.atualizadoEm)}. A sincronização com o Trixlog não sobrescreve as notas.`}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
                >
                    <i className="fa fa-lock text-[9px]" /> Editada
                </span>
                : <span className="text-muted-foreground">—</span>,
        },
        {
            key: "ajusteStatus", label: "Ajuste", sortable: true,
            render: (v) => v
                ? <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{v}</span>
                : "—",
        },
        {
            key: "acoes", label: "Ações", sortable: false, filterable: false, noSelected: true,
            render: (_, row) => (
                <div className="flex gap-1.5">
                    <button
                        type="button"
                        onClick={() => abrirEdicao(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-primary/20 bg-primary/5 px-2 py-1 text-[10px] font-semibold text-primary hover:bg-primary/10"
                    >
                        <i className="fa fa-pencil" /> Editar
                    </button>
                    <button
                        type="button"
                        onClick={() => excluir(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-destructive/20 bg-destructive/5 px-2 py-1 text-[10px] font-semibold text-destructive hover:bg-destructive/10"
                    >
                        <i className="fa fa-trash" /> Excluir
                    </button>
                </div>
            ),
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], []);

    const tableHeader = (
        <div className="flex flex-wrap items-center gap-2 px-1 py-1">
            <div className="relative">
                <i className="fa fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground" />
                <input
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar funcionário, código ou condutor..."
                    className="w-72 rounded-lg border border-input bg-muted py-1.5 pl-7 pr-3 text-xs focus:border-primary focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
            </div>
            <select value={origem} onChange={e => setOrigem(e.target.value)} className="rounded-lg border border-input bg-card px-2 py-1.5 text-xs outline-none">
                <option value="">Todas as origens</option>
                <option value="TRIXLOG">Trixlog</option>
                <option value="MANUAL">Manual</option>
                <option value="ARQUIVO">Planilha</option>
            </select>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <input type="checkbox" checked={somenteEditadas} onChange={e => setSomenteEditadas(e.target.checked)} />
                Somente editadas
            </label>
        </div>
    );

    const botaoSecundario = "inline-flex items-center gap-2 rounded-lg border border-border bg-secondary px-3 py-2 text-xs font-semibold text-white shadow-xs hover:bg-muted";

    return (
        <div className="bg-background px-4 py-3">
            <div className="mx-auto max-w-[1600px] space-y-4">

                {/* Cabeçalho */}
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
                            <i className="fa fa-tachometer-alt text-lg" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">Telemetria</h1>
                            <p className="text-sm text-muted-foreground">Ranking diário dos condutores (segurança viária, manutenção e performance)</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={abrirTrixlog} className={`${botaoSecundario} border-primary/30 bg-primary/10 text-white`}>
                            <i className="fa fa-sync" /> Sincronizar Trixlog
                        </button>
                        <button type="button" onClick={abrirImportacao} className={botaoSecundario}>
                            <i className="fa fa-upload" /> Importar planilha
                        </button>
                        <button type="button" onClick={abrirNova} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground shadow-xs hover:brightness-95">
                            <i className="fa fa-plus" /> Nova medição
                        </button>
                    </div>
                </motion.div>

                {/* Filtros */}
                <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-card">
                    <Campo label="De">
                        <input type="date" value={periodo.inicio} onChange={e => setPeriodo(p => ({ ...p, inicio: e.target.value }))} className={inputCls} />
                    </Campo>
                    <Campo label="Até">
                        <input type="date" value={periodo.fim} onChange={e => setPeriodo(p => ({ ...p, fim: e.target.value }))} className={inputCls} />
                    </Campo>
                    <Campo label="Filial">
                        <select value={filialId} onChange={e => setFilialId(e.target.value)} className={inputCls}>
                            <option value="">Todas as filiais</option>
                            {filiais.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                        </select>
                    </Campo>
                    <button type="button" onClick={carregar} disabled={loading} className={botaoSecundario}>
                        <i className={`fa fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Recarregar
                    </button>
                </div>

                {/* Indicadores */}
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                    <Kpi label="Medições" valor={kpis.total.toLocaleString("pt-BR")} icon="fa fa-list" cls="border-primary/20 bg-primary/5 text-primary" />
                    <Kpi label="Funcionários" valor={kpis.funcionarios} icon="fa fa-users" cls="border-border bg-card text-foreground" />
                    <Kpi
                        label="Média seg. viária" valor={fmtNota(kpis.media)} icon="fa fa-shield-alt"
                        cls={kpis.media >= META_SEGURANCA_VIARIA ? "border-success/30 bg-success/5 text-success" : "border-destructive/20 bg-destructive/5 text-destructive"}
                    />
                    <Kpi label="Editadas à mão" valor={kpis.editadas} icon="fa fa-lock" cls="border-amber-500/20 bg-amber-500/5 text-amber-700" />
                    <Kpi label="Sem vínculo" valor={kpis.semFuncionario} icon="fa fa-unlink" cls="border-amber-500/20 bg-amber-500/5 text-amber-700" />
                </div>

                {/* Tabela */}
                <CustomTable
                    data={filtradas}
                    columns={columns}
                    loading={loading}
                    header={tableHeader}
                    pagination
                    pageSize={20}
                    exportable
                    exportFilename={`Telemetria ${periodo.inicio} a ${periodo.fim}`}
                    className="text-[11px]"
                />
            </div>

            {/* ── Cadastro / edição ── */}
            <Dialog
                visible={modalForm}
                onHide={() => !salvando && setModalForm(false)}
                modal
                blockScroll
                className="w-[560px] max-w-[95%]"
                contentClassName="pt-3"
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className={`fa ${editando ? "fa-pencil text-primary" : "fa-plus-circle text-success"}`} />
                        {editando ? "Editar medição" : "Nova medição"}
                    </div>
                }
            >
                <div className="space-y-3">
                    {editando && (
                        <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700">
                            <i className="fa fa-lock mr-1.5" />
                            Ao salvar, a medição fica protegida: a sincronização com o Trixlog não sobrescreve as notas dela.
                        </p>
                    )}

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Campo label="Filial">
                            <Dropdown
                                value={form.filialId}
                                options={filiais}
                                optionLabel="nome"
                                optionValue="id"
                                onChange={e => alterarFilial(e.value)}
                                placeholder="Selecione"
                                className="w-full border border-input"
                            />
                        </Campo>
                        <Campo label="Funcionário">
                            <Dropdown
                                value={form.funcionarioId}
                                options={funcionarios}
                                optionLabel="nome"
                                optionValue="id"
                                filter
                                onChange={e => setForm(f => ({ ...f, funcionarioId: e.value }))}
                                placeholder={form.filialId ? "Selecione" : "Escolha a filial"}
                                disabled={!form.filialId}
                                className="w-full border border-input"
                            />
                        </Campo>
                    </div>

                    <Campo
                        label="Data"
                        dica={editando?.origem === "TRIXLOG" ? "A data de uma medição do Trixlog não pode ser alterada." : undefined}
                    >
                        <input
                            type="date"
                            value={form.data ?? ""}
                            onChange={e => setForm(f => ({ ...f, data: e.target.value }))}
                            disabled={editando?.origem === "TRIXLOG"}
                            className={inputCls}
                        />
                    </Campo>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {[
                            ["segurancaViaria", "Segurança viária *"],
                            ["manutencao", "Manutenção"],
                            ["performance", "Performance"],
                        ].map(([campo, rotulo]) => (
                            <Campo key={campo} label={rotulo}>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={form[campo] ?? ""}
                                    onChange={e => setForm(f => ({ ...f, [campo]: e.target.value }))}
                                    placeholder="0 a 100"
                                    className={inputCls}
                                />
                            </Campo>
                        ))}
                    </div>

                    {erroForm && (
                        <p className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                            <i className="fa fa-exclamation-triangle mr-1.5" />
                            {erroForm}
                        </p>
                    )}

                    <div className="flex justify-end gap-2 border-t border-border pt-3">
                        <button type="button" onClick={() => setModalForm(false)} disabled={salvando} className={botaoSecundario}>Cancelar</button>
                        <button
                            type="button"
                            onClick={salvar}
                            disabled={salvando}
                            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:brightness-95 disabled:opacity-60"
                        >
                            <i className={salvando ? "fa fa-spinner fa-spin" : "fa fa-save"} /> Salvar
                        </button>
                    </div>
                </div>
            </Dialog>

            {/* ── Importação ── */}
            <Dialog
                visible={modalImportar}
                onHide={() => !importando && setModalImportar(false)}
                modal
                blockScroll
                className="w-[600px] max-w-[95%]"
                contentClassName="pt-3"
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-upload text-primary" /> Importar medições por planilha
                    </div>
                }
            >
                <div className="space-y-3">
                    <div className="space-y-1.5 rounded-lg border border-border bg-muted px-4 py-3 text-xs text-muted-foreground">
                        <p>
                            Colunas: <strong className="text-foreground">Data</strong>, <strong className="text-foreground">Código do Funcionário</strong>,
                            Nome (opcional), <strong className="text-foreground">Segurança Viária</strong>, Manutenção e Performance (notas de 0 a 100).
                        </p>
                        <p>
                            O funcionário é localizado pelo código dentro da filial. Se já existe medição dele no dia, ela é atualizada
                            (campos em branco ficam como estão); senão é criada. As importadas ficam protegidas da sincronização com o Trixlog.
                        </p>
                        <button type="button" onClick={baixarModelo} className="font-semibold text-primary hover:underline">
                            <i className="fa fa-download mr-1" /> Baixar planilha modelo
                        </button>
                    </div>

                    <Campo label="Filial dos funcionários">
                        <select value={filialImportacao} onChange={e => setFilialImportacao(e.target.value)} disabled={importando} className={inputCls}>
                            <option value="">Selecione</option>
                            {filiais.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                        </select>
                    </Campo>

                    <Campo label="Planilha (.xlsx)">
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={e => setArquivo(e.target.files?.[0] ?? null)}
                            disabled={importando}
                            className="text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-primary"
                        />
                    </Campo>

                    {resultadoImportacao && (
                        <div className="space-y-2 rounded-lg border border-border bg-card px-4 py-3 text-xs">
                            <p className="font-semibold text-foreground">
                                <i className="fa fa-check-circle mr-1.5 text-success" />
                                {resultadoImportacao.criadas} criada(s), {resultadoImportacao.atualizadas} atualizada(s)
                                {resultadoImportacao.ignoradas > 0 && <>, <span className="text-destructive">{resultadoImportacao.ignoradas} ignorada(s)</span></>}
                                {" "}de {resultadoImportacao.totalLinhas} linha(s).
                            </p>
                            {resultadoImportacao.erros?.length > 0 && (
                                <ul className="max-h-40 list-disc space-y-0.5 overflow-auto pl-4 text-destructive">
                                    {resultadoImportacao.erros.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            )}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 border-t border-border pt-3">
                        <button type="button" onClick={() => setModalImportar(false)} disabled={importando} className={botaoSecundario}>Fechar</button>
                        <button
                            type="button"
                            onClick={importar}
                            disabled={importando}
                            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:brightness-95 disabled:opacity-60"
                        >
                            <i className={importando ? "fa fa-spinner fa-spin" : "fa fa-upload"} /> {importando ? "Importando..." : "Importar"}
                        </button>
                    </div>
                </div>
            </Dialog>

            {/* ── Sincronização com o Trixlog ── */}
            <Dialog
                visible={modalTrixlog}
                onHide={() => !sincronizando && setModalTrixlog(false)}
                modal
                blockScroll
                className="w-[560px] max-w-[95%]"
                contentClassName="pt-3"
                header={
                    <div className="flex items-center gap-2 text-base font-bold text-foreground">
                        <i className="fa fa-sync text-primary" /> Sincronizar com o Trixlog
                    </div>
                }
            >
                <div className="space-y-3">
                    <p className="rounded-lg border border-border bg-muted px-4 py-3 text-xs text-muted-foreground">
                        Busca no Trixlog o ranking de cada dia do período (até 62 dias) e vincula os condutores aos funcionários.
                        Medições <strong className="text-foreground">editadas à mão, cadastradas ou importadas</strong> são preservadas:
                        a sincronização não sobrescreve as notas delas. Pode levar alguns minutos.
                    </p>

                    <div className="grid grid-cols-2 gap-3">
                        <Campo label="De">
                            <input type="date" value={periodoTrixlog.inicio} disabled={sincronizando}
                                onChange={e => setPeriodoTrixlog(p => ({ ...p, inicio: e.target.value }))} className={inputCls} />
                        </Campo>
                        <Campo label="Até">
                            <input type="date" value={periodoTrixlog.fim} disabled={sincronizando}
                                onChange={e => setPeriodoTrixlog(p => ({ ...p, fim: e.target.value }))} className={inputCls} />
                        </Campo>
                    </div>

                    {sincronizando && (
                        <p className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-primary">
                            <i className="fa fa-spinner fa-spin mr-1.5" />
                            Sincronizando com o Trixlog... não feche esta janela.
                        </p>
                    )}

                    {erroTrixlog && (
                        <p className="rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                            <i className="fa fa-exclamation-triangle mr-1.5" />
                            {erroTrixlog}
                        </p>
                    )}

                    {resultadoTrixlog && (
                        <div className="space-y-2 rounded-lg border border-success/30 bg-success/5 px-4 py-3 text-xs">
                            <p className="font-semibold text-success">
                                <i className="fa fa-check-circle mr-1.5" />
                                Sincronização concluída ({fmtData(resultadoTrixlog.inicio)} a {fmtData(resultadoTrixlog.fim)})
                            </p>
                            <p className="text-foreground">
                                {resultadoTrixlog.criadas} criada(s), {resultadoTrixlog.atualizadas} atualizada(s) e{" "}
                                {resultadoTrixlog.preservadas} preservada(s) por edição manual.
                            </p>
                            {resultadoTrixlog.avisos?.length > 0 && (
                                <div>
                                    <p className="font-semibold text-amber-700">Avisos:</p>
                                    <ul className="max-h-32 list-disc overflow-auto pl-4 text-muted-foreground">
                                        {resultadoTrixlog.avisos.map((a, i) => <li key={i}>{a}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 border-t border-border pt-3">
                        <button type="button" onClick={() => setModalTrixlog(false)} disabled={sincronizando} className={botaoSecundario}>Fechar</button>
                        <button
                            type="button"
                            onClick={sincronizar}
                            disabled={sincronizando}
                            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:brightness-95 disabled:opacity-60"
                        >
                            <i className={sincronizando ? "fa fa-spinner fa-spin" : "fa fa-sync"} /> {sincronizando ? "Sincronizando..." : "Sincronizar"}
                        </button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
